/**
 * LocalStorageProvider
 * Persistent implementation of IDataProvider using browser localStorage
 */

import { nanoid } from 'nanoid'
import type {
  Book,
  CreateBookDto,
  UpdateBookDto,
  Chapter,
  CreateChapterDto,
  UpdateChapterDto,
  Content,
  ExportOptions,
  ExportResult,
} from '@/core/models'
import type { IDataProvider, BookStatistics } from '@/core/interfaces'

const STORAGE_KEYS = {
  BOOKS: 'ebook-genius:books',
  CHAPTERS: 'ebook-genius:chapters',
  CONTENT: 'ebook-genius:content',
  VERSION: 'ebook-genius:version',
}

const CURRENT_VERSION = '1.0.0'

export class LocalStorageProvider implements IDataProvider {
  constructor() {
    this.initializeStorage()
  }

  // ==================== Helper Methods ====================

  private initializeStorage(): void {
    const version = localStorage.getItem(STORAGE_KEYS.VERSION)
    if (!version || version !== CURRENT_VERSION) {
      // Initialize with empty data
      this.saveBooks(new Map())
      this.saveChapters(new Map())
      this.saveContent(new Map())
      localStorage.setItem(STORAGE_KEYS.VERSION, CURRENT_VERSION)
    }
  }

  private loadBooks(): Map<string, Book> {
    const data = localStorage.getItem(STORAGE_KEYS.BOOKS)
    if (!data) return new Map()

    try {
      const parsed = JSON.parse(data)
      return new Map(
        Object.entries(parsed).map(([id, book]: [string, any]) => [
          id,
          {
            ...book,
            createdAt: new Date(book.createdAt),
            updatedAt: new Date(book.updatedAt),
            publishedAt: book.publishedAt ? new Date(book.publishedAt) : undefined,
          },
        ]),
      )
    } catch (error) {
      console.error('Error loading books from localStorage:', error)
      return new Map()
    }
  }

  private saveBooks(books: Map<string, Book>): void {
    const obj = Object.fromEntries(books)
    localStorage.setItem(STORAGE_KEYS.BOOKS, JSON.stringify(obj))
  }

  private loadChapters(): Map<string, Chapter> {
    const data = localStorage.getItem(STORAGE_KEYS.CHAPTERS)
    if (!data) return new Map()

    try {
      const parsed = JSON.parse(data)
      return new Map(
        Object.entries(parsed).map(([id, chapter]: [string, any]) => [
          id,
          {
            ...chapter,
            createdAt: new Date(chapter.createdAt),
            updatedAt: new Date(chapter.updatedAt),
          },
        ]),
      )
    } catch (error) {
      console.error('Error loading chapters from localStorage:', error)
      return new Map()
    }
  }

  private saveChapters(chapters: Map<string, Chapter>): void {
    const obj = Object.fromEntries(chapters)
    localStorage.setItem(STORAGE_KEYS.CHAPTERS, JSON.stringify(obj))
  }

  private loadContent(): Map<string, Content> {
    const data = localStorage.getItem(STORAGE_KEYS.CONTENT)
    if (!data) return new Map()

    try {
      const parsed = JSON.parse(data)
      return new Map(
        Object.entries(parsed).map(([id, content]: [string, any]) => [
          id,
          {
            ...content,
            lastSaved: new Date(content.lastSaved),
          },
        ]),
      )
    } catch (error) {
      console.error('Error loading content from localStorage:', error)
      return new Map()
    }
  }

  private saveContent(content: Map<string, Content>): void {
    const obj = Object.fromEntries(content)
    localStorage.setItem(STORAGE_KEYS.CONTENT, JSON.stringify(obj))
  }

  private calculateWordCount(text: string): number {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length
  }

  // ==================== Book Operations ====================

  async getBooks(): Promise<Book[]> {
    const books = this.loadBooks()
    return Array.from(books.values()).sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
    )
  }

  async getBook(id: string): Promise<Book> {
    const books = this.loadBooks()
    const book = books.get(id)
    if (!book) {
      throw new Error(`Book with id "${id}" not found`)
    }
    return { ...book }
  }

  async createBook(dto: CreateBookDto): Promise<Book> {
    const books = this.loadBooks()
    const now = new Date()

    const book: Book = {
      id: nanoid(),
      title: dto.title,
      author: dto.author,
      description: dto.description || '',
      createdAt: now,
      updatedAt: now,
      status: 'draft',
      metadata: {
        language: dto.language || 'en',
        genre: dto.genre || [],
        tags: dto.tags || [],
        wordCount: 0,
      },
      chapterIds: [],
    }

    books.set(book.id, book)
    this.saveBooks(books)

    return { ...book }
  }

  async updateBook(id: string, updates: UpdateBookDto): Promise<Book> {
    const books = this.loadBooks()
    const book = books.get(id)
    if (!book) {
      throw new Error(`Book with id "${id}" not found`)
    }

    const updatedBook: Book = {
      ...book,
      ...updates,
      metadata: {
        ...book.metadata,
        ...updates.metadata,
      },
      updatedAt: new Date(),
    }

    books.set(id, updatedBook)
    this.saveBooks(books)

    return { ...updatedBook }
  }

  async deleteBook(id: string): Promise<void> {
    const books = this.loadBooks()
    const book = books.get(id)
    if (!book) {
      throw new Error(`Book with id "${id}" not found`)
    }

    const chapters = this.loadChapters()
    const content = this.loadContent()

    // Delete all chapters and content
    for (const chapterId of book.chapterIds) {
      chapters.delete(chapterId)
      content.delete(chapterId)
    }

    books.delete(id)

    this.saveBooks(books)
    this.saveChapters(chapters)
    this.saveContent(content)
  }

  // ==================== Chapter Operations ====================

  async getChapters(bookId: string): Promise<Chapter[]> {
    const books = this.loadBooks()
    const book = books.get(bookId)
    if (!book) {
      throw new Error(`Book with id "${bookId}" not found`)
    }

    const chapters = this.loadChapters()
    return book.chapterIds
      .map(id => chapters.get(id))
      .filter((chapter): chapter is Chapter => chapter !== undefined)
      .sort((a, b) => a.order - b.order)
  }

  async getChapter(id: string): Promise<Chapter> {
    const chapters = this.loadChapters()
    const chapter = chapters.get(id)
    if (!chapter) {
      throw new Error(`Chapter with id "${id}" not found`)
    }
    return { ...chapter }
  }

  async createChapter(dto: CreateChapterDto): Promise<Chapter> {
    const books = this.loadBooks()
    const book = books.get(dto.bookId)
    if (!book) {
      throw new Error(`Book with id "${dto.bookId}" not found`)
    }

    const chapters = this.loadChapters()
    const content = this.loadContent()
    const now = new Date()
    const order = dto.order ?? book.chapterIds.length + 1

    const chapter: Chapter = {
      id: nanoid(),
      bookId: dto.bookId,
      title: dto.title,
      order,
      content: dto.content || '',
      wordCount: this.calculateWordCount(dto.content || ''),
      status: 'draft',
      createdAt: now,
      updatedAt: now,
    }

    chapters.set(chapter.id, chapter)

    // Initialize content
    const chapterContent: Content = {
      chapterId: chapter.id,
      text: chapter.content,
      formatting: [],
      version: 1,
      lastSaved: now,
      autoSaveEnabled: true,
    }
    content.set(chapter.id, chapterContent)

    // Add to book
    book.chapterIds.push(chapter.id)
    book.updatedAt = now
    books.set(book.id, book)

    this.saveBooks(books)
    this.saveChapters(chapters)
    this.saveContent(content)

    return { ...chapter }
  }

  async updateChapter(id: string, updates: UpdateChapterDto): Promise<Chapter> {
    const chapters = this.loadChapters()
    const chapter = chapters.get(id)
    if (!chapter) {
      throw new Error(`Chapter with id "${id}" not found`)
    }

    const updatedChapter: Chapter = {
      ...chapter,
      ...updates,
      wordCount: updates.content
        ? this.calculateWordCount(updates.content)
        : chapter.wordCount,
      updatedAt: new Date(),
    }

    chapters.set(id, updatedChapter)
    this.saveChapters(chapters)

    // Update book's updatedAt
    const books = this.loadBooks()
    const book = books.get(chapter.bookId)
    if (book) {
      book.updatedAt = new Date()
      books.set(book.id, book)
      this.saveBooks(books)
    }

    return { ...updatedChapter }
  }

  async deleteChapter(id: string): Promise<void> {
    const chapters = this.loadChapters()
    const chapter = chapters.get(id)
    if (!chapter) {
      throw new Error(`Chapter with id "${id}" not found`)
    }

    const books = this.loadBooks()
    const content = this.loadContent()

    // Remove from book
    const book = books.get(chapter.bookId)
    if (book) {
      book.chapterIds = book.chapterIds.filter(chapterId => chapterId !== id)
      book.updatedAt = new Date()
      books.set(book.id, book)
      this.saveBooks(books)
    }

    chapters.delete(id)
    content.delete(id)

    this.saveChapters(chapters)
    this.saveContent(content)
  }

  async reorderChapters(bookId: string, chapterIds: string[]): Promise<Chapter[]> {
    const books = this.loadBooks()
    const book = books.get(bookId)
    if (!book) {
      throw new Error(`Book with id "${bookId}" not found`)
    }

    const chapters = this.loadChapters()
    const updatedChapters: Chapter[] = []

    chapterIds.forEach((chapterId, index) => {
      const chapter = chapters.get(chapterId)
      if (chapter && chapter.bookId === bookId) {
        const updatedChapter = {
          ...chapter,
          order: index + 1,
          updatedAt: new Date(),
        }
        chapters.set(chapterId, updatedChapter)
        updatedChapters.push(updatedChapter)
      }
    })

    book.chapterIds = chapterIds
    book.updatedAt = new Date()
    books.set(bookId, book)

    this.saveBooks(books)
    this.saveChapters(chapters)

    return updatedChapters
  }

  // ==================== Content Operations ====================

  async getContent(chapterId: string): Promise<Content> {
    const content = this.loadContent()
    let chapterContent = content.get(chapterId)

    if (!chapterContent) {
      const chapters = this.loadChapters()
      const chapter = chapters.get(chapterId)
      if (!chapter) {
        throw new Error(`Chapter with id "${chapterId}" not found`)
      }

      chapterContent = {
        chapterId,
        text: chapter.content,
        formatting: [],
        version: 1,
        lastSaved: new Date(),
        autoSaveEnabled: true,
      }
      content.set(chapterId, chapterContent)
      this.saveContent(content)
    }

    return { ...chapterContent }
  }

  async updateContent(chapterId: string, updates: Partial<Content>): Promise<Content> {
    const content = this.loadContent()
    let chapterContent = content.get(chapterId)

    if (!chapterContent) {
      const chapters = this.loadChapters()
      const chapter = chapters.get(chapterId)
      if (!chapter) {
        throw new Error(`Chapter with id "${chapterId}" not found`)
      }

      chapterContent = {
        chapterId,
        text: '',
        formatting: [],
        version: 1,
        lastSaved: new Date(),
        autoSaveEnabled: true,
      }
    }

    const updatedContent: Content = {
      ...chapterContent,
      ...updates,
      version: chapterContent.version + 1,
      lastSaved: new Date(),
    }

    content.set(chapterId, updatedContent)
    this.saveContent(content)

    // Update chapter content and word count
    if (updates.text !== undefined) {
      const chapters = this.loadChapters()
      const chapter = chapters.get(chapterId)
      if (chapter) {
        chapter.content = updates.text
        chapter.wordCount = this.calculateWordCount(updates.text)
        chapter.updatedAt = new Date()
        chapters.set(chapterId, chapter)
        this.saveChapters(chapters)
      }
    }

    return { ...updatedContent }
  }

  // ==================== Search Operations ====================

  async searchBooks(query: string): Promise<Book[]> {
    const books = this.loadBooks()
    const lowerQuery = query.toLowerCase()

    return Array.from(books.values()).filter(
      book =>
        book.title.toLowerCase().includes(lowerQuery) ||
        book.author.toLowerCase().includes(lowerQuery) ||
        book.description.toLowerCase().includes(lowerQuery),
    )
  }

  async searchChapters(bookId: string, query: string): Promise<Chapter[]> {
    const books = this.loadBooks()
    const book = books.get(bookId)
    if (!book) {
      throw new Error(`Book with id "${bookId}" not found`)
    }

    const chapters = this.loadChapters()
    const lowerQuery = query.toLowerCase()

    return Array.from(chapters.values())
      .filter(
        chapter =>
          chapter.bookId === bookId &&
          (chapter.title.toLowerCase().includes(lowerQuery) ||
            chapter.content.toLowerCase().includes(lowerQuery)),
      )
      .sort((a, b) => a.order - b.order)
  }

  // ==================== Export Operations ====================

  async exportBook(bookId: string, options: ExportOptions): Promise<ExportResult> {
    const book = await this.getBook(bookId)
    const chapters = await this.getChapters(bookId)

    // Get chapter contents
    const chapterContents = chapters.map(chapter => chapter.content)

    // Import export utilities dynamically
    const {
      exportAsMarkdown,
      exportAsHTML,
      exportAsPDF,
      exportAsEPUB,
    } = await import('../../utils/export')

    let result: { content: string | Blob; filename: string; mimeType: string }

    try {
      switch (options.format) {
        case 'pdf':
          result = await exportAsPDF(book, chapters, chapterContents)
          break
        case 'epub':
          result = await exportAsEPUB(book, chapters, chapterContents)
          break
        case 'markdown':
          result = await exportAsMarkdown(book, chapters, chapterContents)
          break
        case 'html':
          result = await exportAsHTML(book, chapters, chapterContents)
          break
        default:
          // Fallback to simple text
          let text = `${book.title}\nby ${book.author}\n\n${book.description}\n\n`
          for (let i = 0; i < chapters.length; i++) {
            text += `\n\n${chapters[i].title}\n\n${chapterContents[i]}\n`
          }
          result = {
            content: text,
            filename: `${book.title.replace(/\s+/g, '_')}.txt`,
            mimeType: 'text/plain',
          }
      }

      // Convert content to blob if it's a string
      const blob =
        result.content instanceof Blob
          ? result.content
          : new Blob([result.content], { type: result.mimeType })

      return {
        success: true,
        blob,
        filename: result.filename,
      }
    } catch (error) {
      console.error('Export failed:', error)
      return {
        success: false,
        filename: '',
        error: error instanceof Error ? error.message : 'Export failed',
      }
    }
  }

  // ==================== Utility Operations ====================

  async getBookWordCount(bookId: string): Promise<number> {
    const chapters = await this.getChapters(bookId)
    return chapters.reduce((total, chapter) => total + chapter.wordCount, 0)
  }

  async getBookStatistics(bookId: string): Promise<BookStatistics> {
    const chapters = await this.getChapters(bookId)

    const totalWords = chapters.reduce((total, chapter) => total + chapter.wordCount, 0)
    const totalCharacters = chapters.reduce(
      (total, chapter) => total + chapter.content.length,
      0,
    )
    const completedChapters = chapters.filter(c => c.status === 'completed').length
    const draftChapters = chapters.filter(c => c.status === 'draft').length

    return {
      totalWords,
      totalCharacters,
      totalChapters: chapters.length,
      averageWordsPerChapter: chapters.length > 0 ? Math.round(totalWords / chapters.length) : 0,
      estimatedReadingTime: Math.ceil(totalWords / 200),
      completedChapters,
      draftChapters,
    }
  }
}
